import {
    Authenticator,
    IconsProvider,
    PasswordField,
    Theme as AmplifyTheme,
    ThemeProvider as AmplifyThemeProvider,
    useAuthenticator,
} from '@aws-amplify/ui-react'
import { Typography } from 'ComponentLibrary/src/piazza'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { theme } from 'ComponentLibrary/src/styles'
import { Box, GlobalStyles, Stack, styled } from '@mui/material'
import type { ChangeEvent, FocusEvent } from 'react'
import { ComponentProps, useCallback, useEffect, useRef, useState } from 'react'
import { sendDebugInfoToSentry, sendToSentry } from 'ComponentLibrary'
import type { ConfirmResetPasswordInput, ConfirmSignInInput, SignInInput } from 'aws-amplify/auth'
import { confirmResetPassword, confirmSignIn, signIn } from 'aws-amplify/auth'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { EyeIcon, EyeSlashIcon } from '@phosphor-icons/react'
import {
    AuthenticationFooter,
    AuthenticationHeader,
    AuthenticationSupportNote,
    DEFAULT_AUTH_BACKGROUND_POSITION,
} from './AuthenticationPageLayout'

type AuthenticatorServices = ComponentProps<typeof Authenticator>['services']

export type AuthenticatedAppTranslations = {
    supportNote: {
        prefix: string
        suffix: string
    }
    formFields: {
        email: string
        password: string
        code: string
        newPassword: string
        confirmNewPassword: string
    }
    errors: {
        signIn: string
        forgotPasswordSubmit: string
        confirmSignIn: string
    }
    signIn: {
        title: string
        description: string
        forgotPassword: string
    }
    forgotPassword: {
        title: string
        description: string
    }
    confirmResetPassword: {
        title: string
        description: string
    }
    forceNewPassword: {
        title: string
        description: string
        minLengthError: string
        mismatchError: string
    }
}

const defaultAuthenticatedAppTranslations: AuthenticatedAppTranslations = {
    supportNote: {
        prefix: 'Bei Fragen stehen wir Ihnen unter',
        suffix: 'zur Verfügung.',
    },
    formFields: {
        email: 'E-Mail',
        password: 'Passwort',
        code: 'Code',
        newPassword: 'Neues Passwort',
        confirmNewPassword: 'Neues Passwort wiederholen',
    },
    errors: {
        signIn: 'Fehler bei der Anmeldung. Bitte überprüfen Sie Ihre Eingaben und versuchen Sie es erneut.',
        forgotPasswordSubmit: 'Fehler bei der Passwortänderung. Bitte überprüfen Sie Ihre Eingaben und versuchen Sie es erneut.',
        confirmSignIn: 'Fehler bei der Anmeldung. Bitte überprüfen Sie Ihre Eingaben und versuchen Sie es erneut.',
    },
    signIn: {
        title: 'Willkommen',
        description: 'Effizient managen. Informiert entscheiden.',
        forgotPassword: 'Passwort vergessen?',
    },
    forgotPassword: {
        title: 'Passwort zurücksetzen',
        description: 'Geben Sie Ihre E-Mail ein, um einen Code für das Zurücksetzen Ihres Passworts zu erhalten.',
    },
    confirmResetPassword: {
        title: 'Passwort zurücksetzen',
        description: 'Geben Sie den Code ein, den Sie per E-Mail erhalten haben und legen Sie Ihr neues Passwort fest.',
    },
    forceNewPassword: {
        title: 'Passwort festlegen',
        description: 'Legen Sie ein neues Passwort fest, um Ihre Anmeldung abzuschließen.',
        minLengthError: 'Das Passwort muss mind. 8 Zeichen lang sein',
        mismatchError: 'Die Passwörter müssen übereinstimmen',
    },
}

export const getServices = ({
    onSignIn,
    translations = defaultAuthenticatedAppTranslations,
}: Partial<{ onSignIn: AuthenticatedAppProps['onSignIn']; translations: AuthenticatedAppTranslations }>) => ({
    async handleSignIn(input: SignInInput) {
        try {
            try {
                sessionStorage.setItem('is-new-login', 'true')
            } catch (error) {
                sendToSentry(error) // Probably user has blocked SessionStorage in their browser
            }

            const trimmedInput = {
                ...input,
                username: input.username.trim(),
                password: input.password?.trim(),
            }

            const result = await signIn(trimmedInput)
            await onSignIn?.()

            return result
        } catch (error) {
            sendDebugInfoToSentry(error)
            throw new Error(translations.errors.signIn)
        }
    },
    async handleForgotPasswordSubmit(input: ConfirmResetPasswordInput) {
        try {
            return await confirmResetPassword(input)
        } catch (error) {
            sendDebugInfoToSentry(error)
            throw new Error(translations.errors.forgotPasswordSubmit)
        }
    },
    async handleConfirmSignIn(input: ConfirmSignInInput) {
        try {
            return await confirmSignIn(input)
        } catch (error) {
            sendDebugInfoToSentry(error)
            throw new Error(translations.errors.confirmSignIn)
        }
    },
})

type AuthenticatedAppProps = {
    children: ComponentProps<typeof Authenticator>['children']
    authLogoSrc?: string
    authBackgroundSrc?: string
    authBackgroundPosition?: string
    authSubtitle?: string
    onSignIn?: () => Promise<void>
    translations?: AuthenticatedAppTranslations
}

const AuthenticatedApp = ({
    children,
    authLogoSrc = '/einwertLogo.png',
    authBackgroundSrc,
    authBackgroundPosition = DEFAULT_AUTH_BACKGROUND_POSITION,
    authSubtitle = 'Value Management Platform',
    onSignIn,
    translations = defaultAuthenticatedAppTranslations,
}: AuthenticatedAppProps) => {
    return (
        <MuiThemeProvider theme={theme}>
            <AmplifyThemeProvider theme={AmplifyUiTheme}>
                <IconsProvider icons={AmplifyIcons}>
                    <AmplifyAuthenticatorOverrides />
                    <StyledAuthenticator
                        loginMechanisms={['email']}
                        hideSignUp
                        services={
                            // Amplify UI passes only input, but its service types inherit the SDK's context overloads.
                            getServices({ onSignIn, translations }) as AuthenticatorServices
                        }
                        components={{
                            Header: () => (
                                <AuthenticationHeader
                                    authLogoSrc={authLogoSrc}
                                    authBackgroundSrc={authBackgroundSrc}
                                    authBackgroundPosition={authBackgroundPosition}
                                    authSubtitle={authSubtitle}
                                />
                            ),
                            Footer: AuthenticationFooter,
                            SignIn: {
                                Header: () => <SignInIntro translations={translations} />,
                                Footer: () => <SignInFooter translations={translations} />,
                            },
                            ForgotPassword: {
                                Header: () => <ForgotPasswordIntro translations={translations} />,
                                Footer: () => <SupportFooterNote translations={translations} />,
                            },
                            ConfirmResetPassword: {
                                Header: () => <ConfirmResetPasswordIntro translations={translations} />,
                                Footer: () => <SupportFooterNote translations={translations} />,
                            },
                            ForceNewPassword: {
                                Header: () => <ForceNewPasswordIntro translations={translations} />,
                                FormFields: () => <ForceNewPasswordFormFields translations={translations} />,
                                Footer: () => <SupportFooterNote translations={translations} />,
                            },
                        }}
                        formFields={{
                            signIn: {
                                username: {
                                    placeholder: translations.formFields.email,
                                    label: translations.formFields.email,
                                    labelHidden: true,
                                    type: 'text',
                                },
                                password: {
                                    placeholder: translations.formFields.password,
                                    label: translations.formFields.password,
                                    labelHidden: true,
                                    type: 'password',
                                },
                            },
                            forgotPassword: {
                                username: {
                                    placeholder: translations.formFields.email,
                                    label: translations.formFields.email,
                                    labelHidden: true,
                                    type: 'text',
                                },
                            },
                            confirmResetPassword: {
                                confirmation_code: {
                                    placeholder: `${translations.formFields.code}*`,
                                    label: translations.formFields.code,
                                    labelHidden: true,
                                },
                                password: {
                                    placeholder: translations.formFields.newPassword,
                                    label: translations.formFields.newPassword,
                                    labelHidden: true,
                                },
                                confirm_password: {
                                    placeholder: translations.formFields.confirmNewPassword,
                                    label: translations.formFields.confirmNewPassword,
                                    labelHidden: true,
                                },
                            },
                        }}
                    >
                        {(props) => {
                            if (typeof children === 'function') {
                                return <>{children(props)}</>
                            }
                            return <>{children}</>
                        }}
                    </StyledAuthenticator>
                </IconsProvider>
            </AmplifyThemeProvider>
        </MuiThemeProvider>
    )
}

const AmplifyIcons = {
    passwordField: {
        visibility: <EyeIcon aria-hidden="true" size={tokens.SizingIconS} weight="bold" />,
        visibilityOff: <EyeSlashIcon aria-hidden="true" size={tokens.SizingIconS} weight="bold" />,
    },
}

const AUTH_CARD_WIDTH = '478px'
const AUTH_FIELD_HEIGHT = '42px'
const AUTH_PASSWORD_VISIBILITY_BUTTON_WIDTH = '50px'
const AUTH_FORM_PADDING = tokens.SizingSpacing400
const AUTH_FORM_SECTION_MARGIN_BOTTOM = tokens.SizingSpacing400
const AUTH_FORGOT_PASSWORD_LINK_OFFSET = '-26px'

// Reused across all three screens, only the copy (and, for SignIn, the surrounding margin) differs

const AuthIntro = ({
    title,
    description,
    marginTop = tokens.SizingSpacingNone,
    marginBottom = tokens.SizingSpacing300,
    paddingX = tokens.SizingSpacingNone,
}: {
    title: string
    description: string
    marginTop?: string
    marginBottom?: string
    paddingX?: string
}) => (
    <Stack gap={tokens.SizingSpacing100} textAlign="left" marginTop={marginTop} marginBottom={marginBottom} paddingX={paddingX}>
        <Typography variant="headline1" color={tokens.ColorTextBoldest}>
            {title}
        </Typography>
        <Typography variant="textS" color={tokens.ColorTextDefault}>
            {description}
        </Typography>
    </Stack>
)

const SupportFooterNote = ({ translations }: { translations: AuthenticatedAppTranslations }) => (
    <Box marginTop={tokens.SizingSpacing200}>
        <AuthenticationSupportNote prefix={translations.supportNote.prefix} suffix={translations.supportNote.suffix} />
    </Box>
)

// Amplify renders SignIn's Header outside the <form>, so the intro needs to share the form's horizontal padding.
const SignInIntro = ({ translations }: { translations: AuthenticatedAppTranslations }) => (
    <AuthIntro
        title={translations.signIn.title}
        description={translations.signIn.description}
        marginTop={AUTH_FORM_PADDING}
        marginBottom={AUTH_FORM_SECTION_MARGIN_BOTTOM}
        paddingX={AUTH_FORM_PADDING}
    />
)

const ForgotPasswordIntro = ({ translations }: { translations: AuthenticatedAppTranslations }) => (
    <AuthIntro
        title={translations.forgotPassword.title}
        description={translations.forgotPassword.description}
        marginBottom={AUTH_FORM_SECTION_MARGIN_BOTTOM}
    />
)

const ConfirmResetPasswordIntro = ({ translations }: { translations: AuthenticatedAppTranslations }) => (
    <AuthIntro
        title={translations.confirmResetPassword.title}
        description={translations.confirmResetPassword.description}
        marginBottom={AUTH_FORM_SECTION_MARGIN_BOTTOM}
    />
)

const ForceNewPasswordIntro = ({ translations }: { translations: AuthenticatedAppTranslations }) => (
    <AuthIntro
        title={translations.forceNewPassword.title}
        description={translations.forceNewPassword.description}
        marginBottom={AUTH_FORM_SECTION_MARGIN_BOTTOM}
    />
)

type ForceNewPasswordErrors = {
    password?: string
    confirmPassword?: string
}

const getForceNewPasswordErrors = ({
    password,
    confirmPassword,
    translations,
}: {
    password: string
    confirmPassword: string
    translations: AuthenticatedAppTranslations
}): ForceNewPasswordErrors => ({
    password: password.length < 8 ? translations.forceNewPassword.minLengthError : undefined,
    confirmPassword: !confirmPassword || password !== confirmPassword ? translations.forceNewPassword.mismatchError : undefined,
})

const ForceNewPasswordFormFields = ({ translations }: { translations: AuthenticatedAppTranslations }) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const [errors, setErrors] = useState<ForceNewPasswordErrors>({})
    const [touchedFields, setTouchedFields] = useState({ password: false, confirmPassword: false })
    const [hasSubmitted, setHasSubmitted] = useState(false)

    const validateForm = useCallback(() => {
        const form = containerRef.current?.closest('form')
        if (!form) {
            return {}
        }

        const formData = new FormData(form)
        const nextErrors = getForceNewPasswordErrors({
            password: String(formData.get('password') ?? ''),
            confirmPassword: String(formData.get('confirm_password') ?? ''),
            translations,
        })
        setErrors(nextErrors)
        return nextErrors
    }, [translations])

    useEffect(() => {
        const form = containerRef.current?.closest('form')
        if (!form) {
            return undefined
        }

        form.noValidate = true

        const handleSubmit = (event: SubmitEvent) => {
            setHasSubmitted(true)
            const nextErrors = validateForm()

            if (nextErrors.password || nextErrors.confirmPassword) {
                event.preventDefault()
                event.stopImmediatePropagation()
            }
        }

        form.addEventListener('submit', handleSubmit, true)
        return () => {
            form.removeEventListener('submit', handleSubmit, true)
        }
    }, [validateForm])

    const handleFieldChange = () => {
        if (hasSubmitted || touchedFields.password || touchedFields.confirmPassword) {
            validateForm()
        }
    }

    const handleFieldBlur = (field: keyof typeof touchedFields) => () => {
        setTouchedFields((previousTouchedFields) => ({ ...previousTouchedFields, [field]: true }))
        validateForm()
    }

    const showPasswordError = hasSubmitted || touchedFields.password
    const showConfirmPasswordError = hasSubmitted || touchedFields.confirmPassword

    return (
        <Box ref={containerRef} className="amplify-flex" display="flex" flexDirection="column">
            <ValidatedPasswordField
                name="password"
                placeholder={translations.formFields.newPassword}
                label={translations.formFields.newPassword}
                error={showPasswordError ? errors.password : undefined}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur('password')}
            />
            <ValidatedPasswordField
                name="confirm_password"
                placeholder={translations.formFields.confirmNewPassword}
                label={translations.formFields.confirmNewPassword}
                error={showConfirmPasswordError ? errors.confirmPassword : undefined}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur('confirmPassword')}
            />
        </Box>
    )
}

const ValidatedPasswordField = ({
    error,
    label,
    name,
    onBlur,
    onChange,
    placeholder,
}: {
    error?: string
    label: string
    name: string
    onBlur: (event: FocusEvent<HTMLInputElement>) => void
    onChange: (event: ChangeEvent<HTMLInputElement>) => void
    placeholder: string
}) => (
    <Box className="amplify-field">
        <PasswordField
            autoCapitalize="off"
            autoComplete="new-password"
            hasError={!!error}
            isRequired
            label={label}
            labelHidden
            name={name}
            onBlur={onBlur}
            onChange={onChange}
            placeholder={placeholder}
        />
        {error && <Box className="amplify-field__error-message auth-password-error-message">{error}</Box>}
    </Box>
)

// SignIn doesn't share the ForgotPassword/ConfirmResetPassword two-button footer, so it gets its own
// footer that pulls the "Passwort vergessen?" link up next to the submit button, then adds the support note
const SignInFooter = ({ translations }: { translations: AuthenticatedAppTranslations }) => {
    const { toForgotPassword } = useAuthenticator((context) => [context.toForgotPassword])
    return (
        <SignInFooterContent>
            <ForgotPasswordLink type="button" onClick={toForgotPassword}>
                {translations.signIn.forgotPassword}
            </ForgotPasswordLink>
            <SupportFooterNote translations={translations} />
        </SignInFooterContent>
    )
}

const SignInFooterContent = styled(Stack)({
    gap: tokens.SizingSpacing300,
})

const ForgotPasswordLink = styled('button')(() => ({
    marginTop: AUTH_FORGOT_PASSWORD_LINK_OFFSET,
    alignSelf: 'flex-start',
    background: 'none',
    border: 'none',
    padding: tokens.SizingSpacingNone,
    cursor: 'pointer',
    color: tokens.ColorTextLinkDefault,
    ...tokens.Link,
    '&:hover': {
        textDecoration: 'underline',
    },
}))

const authActionButtonStyles = {
    alignItems: 'center',
    backgroundColor: tokens.ColorSurfacePositiveBoldDefault,
    border: `${tokens.SizingBorderS} solid transparent`,
    borderRadius: tokens.SizingCornerS,
    boxSizing: 'border-box',
    color: tokens.ColorTextInverse,
    display: 'inline-flex',
    height: tokens.SizingSpacing400,
    justifyContent: 'center',
    padding: `${tokens.SizingSpacingNone} ${tokens.SizingSpacing100}`,
    ...tokens.Link,
    maxHeight: tokens.SizingSpacing400,
    minHeight: tokens.SizingSpacing400,
    outline: 'none',
    width: '50%',
    '&:hover': {
        backgroundColor: tokens.ColorSurfacePositiveBoldHover,
        color: tokens.ColorTextInverse,
    },
    '&:active': {
        backgroundColor: tokens.ColorSurfacePositiveBoldPressed,
        color: tokens.ColorTextInverse,
    },
    '&:focus, &:focus-visible': {
        backgroundColor: tokens.ColorSurfacePositiveBoldDefault,
        boxShadow: 'none',
        color: tokens.ColorTextInverse,
        outline: 'none',
    },
}

const getAuthLinkButtonStyles = () => ({
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    color: tokens.ColorTextLinkDefault,
    outline: 'none',
    padding: tokens.SizingSpacingNone,
    ...tokens.Link,
    '&:hover': {
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        color: tokens.ColorTextLinkDefault,
        textDecoration: 'underline',
    },
    '&:focus, &:focus-visible': {
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        boxShadow: 'none',
        color: tokens.ColorTextLinkDefault,
        outline: 'none',
    },
})

// Amplify only lets us override the Header/Footer slot of each screen, the actual input/button markup is
// rendered internally. These rules restyle that internal markup via Amplify's documented `data-amplify-*` hooks.
const AmplifyAuthenticatorOverrides = () => (
    <GlobalStyles
        styles={{
            '[data-amplify-authenticator] [data-amplify-router]': {
                borderRadius: tokens.SizingCornerL,
                border: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
                boxShadow: tokens.Popup.boxShadow,
                overflow: 'hidden',
            },
            '[data-amplify-authenticator] [data-amplify-router]:has(.amplify-alert, .amplify-message, .auth-password-error-message)': {
                animation: 'auth-error-vibrate 0.35s linear',
            },
            '@keyframes auth-error-vibrate': {
                '0%, 100%': { transform: 'translateX(0)' },
                '20%, 60%': { transform: `translateX(-${tokens.SizingSpacing050})` },
                '40%, 80%': { transform: `translateX(${tokens.SizingSpacing050})` },
            },
            '[data-amplify-authenticator] .amplify-field-group__control, [data-amplify-authenticator] .amplify-input': {
                borderRadius: tokens.SizingCornerS,
                borderWidth: tokens.SizingBorderS,
                color: tokens.ColorTextBoldest,
                height: AUTH_FIELD_HEIGHT,
                ...tokens.TextS,
                '&::placeholder': {
                    color: tokens.ColorTextSubtle,
                    opacity: 1,
                },
            },
            '[data-amplify-authenticator] .amplify-field-group:focus-within .amplify-field-group__control, [data-amplify-authenticator] .amplify-input:focus':
                {
                    borderColor: tokens.ColorBorderFocused,
                    borderWidth: tokens.SizingBorderS,
                    boxShadow: 'none',
                    outline: 'none',
                },
            "[data-amplify-authenticator] .amplify-input[aria-invalid='true'], [data-amplify-authenticator] .amplify-input[aria-invalid='true']:focus":
                {
                    borderColor: tokens.ColorBorderDanger,
                    borderWidth: tokens.SizingBorderS,
                    color: tokens.ColorTextBoldest,
                    boxShadow: 'none',
                    outline: 'none',
                },
            "[data-amplify-authenticator] .amplify-input[aria-invalid='true']::placeholder": {
                color: tokens.ColorTextSubtle,
                opacity: 1,
            },
            '[data-amplify-authenticator] .amplify-field-group__outer-end .amplify-field-group__control': {
                alignItems: 'center',
                backgroundColor: tokens.ColorSurfaceDefault,
                borderColor: tokens.ColorBorderDefault,
                borderLeftWidth: 0,
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                boxSizing: 'border-box',
                color: tokens.ColorIconBoldest,
                display: 'inline-flex',
                height: AUTH_FIELD_HEIGHT,
                justifyContent: 'center',
                minWidth: AUTH_PASSWORD_VISIBILITY_BUTTON_WIDTH,
                outline: 'none',
                padding: tokens.SizingSpacingNone,
                width: AUTH_PASSWORD_VISIBILITY_BUTTON_WIDTH,
                '&:hover': {
                    backgroundColor: tokens.ColorSurfaceHover,
                    color: tokens.ColorIconBoldest,
                },
                '&:active': {
                    backgroundColor: tokens.ColorSurfacePressed,
                    color: tokens.ColorIconBoldest,
                },
                '& svg': {
                    height: tokens.SizingIconS,
                    width: tokens.SizingIconS,
                },
            },
            '[data-amplify-authenticator] .amplify-field-group:focus-within .amplify-field-group__outer-end .amplify-field-group__control, [data-amplify-authenticator] .amplify-field-group__outer-end .amplify-field-group__control:focus':
                {
                    borderColor: tokens.ColorBorderDefault,
                    color: tokens.ColorIconBoldest,
                    boxShadow: 'none',
                    outline: 'none',
                },
            '[data-amplify-authenticator] button:focus, [data-amplify-authenticator] button:focus-visible': {
                boxShadow: 'none',
                outline: 'none',
            },
            '[data-amplify-authenticator] .amplify-field-group :not(:last-child) .amplify-input': {
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
            },
            '[data-amplify-authenticator-forgotpassword] fieldset, [data-amplify-authenticator-confirmresetpassword] fieldset, [data-amplify-authenticator-forcenewpassword] fieldset':
                {
                    gap: tokens.SizingSpacingNone,
                },
            '[data-amplify-authenticator] .amplify-field__error-message, [data-amplify-authenticator] .amplify-field__error-message *, [data-amplify-authenticator] .amplify-alert, [data-amplify-authenticator] .amplify-alert *, [data-amplify-authenticator] [role="alert"], [data-amplify-authenticator] [role="alert"] *':
                {
                    ...tokens.TextXs,
                    color: tokens.ColorTextDangerSubtle,
                },
            '[data-amplify-authenticator] .amplify-field__error-message': {
                padding: tokens.SizingSpacingNone,
            },
            '[data-amplify-authenticator] .auth-password-error-message': {
                marginLeft: tokens.SizingSpacing200,
                marginTop: tokens.SizingSpacingNone,
            },
            '[data-amplify-authenticator] .amplify-alert, [data-amplify-authenticator] .amplify-message, [data-amplify-authenticator] [role="alert"]':
                {
                    alignItems: 'flex-start',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderRadius: tokens.SizingCornerNone,
                    boxShadow: 'none',
                    gap: tokens.SizingSpacingNone,
                    justifyContent: 'flex-start',
                    padding: tokens.SizingSpacingNone,
                },
            '[data-amplify-authenticator] .amplify-alert__body, [data-amplify-authenticator] .amplify-message__content': {
                display: 'block',
                flex: 'initial',
                gap: tokens.SizingSpacingNone,
                padding: tokens.SizingSpacingNone,
            },
            '[data-amplify-authenticator] .amplify-alert__heading, [data-amplify-authenticator] .amplify-message__heading': {
                display: 'none',
            },
            '[data-amplify-authenticator] .amplify-alert__icon, [data-amplify-authenticator] .amplify-alert__dismiss, [data-amplify-authenticator] .amplify-message__icon, [data-amplify-authenticator] .amplify-message__dismiss':
                {
                    display: 'none',
                },
            '[data-amplify-authenticator] .amplify-alert, [data-amplify-authenticator] .amplify-message': {
                marginBottom: tokens.SizingSpacing300,
            },
            '[data-amplify-authenticator] [data-amplify-authenticator-signin][data-amplify-form]': {
                paddingTop: tokens.SizingSpacingNone,
            },
            '[data-amplify-authenticator-signin] > .amplify-flex': {
                gap: tokens.SizingSpacingNone,
            },
            '[data-amplify-authenticator-signin] fieldset': {
                gap: tokens.SizingSpacing150,
                marginBottom: AUTH_FORM_SECTION_MARGIN_BOTTOM,
            },
            '[data-amplify-authenticator-forcenewpassword] fieldset': {
                display: 'grid !important',
                gridAutoFlow: 'row dense',
                gridTemplateColumns: '1fr 1fr',
                columnGap: tokens.SizingSpacing300,
                rowGap: tokens.SizingSpacingNone,
            },
            '[data-amplify-authenticator-forcenewpassword] fieldset > :not(button)': {
                gridColumn: '1 / -1',
            },
            "[data-amplify-authenticator-forgotpassword] fieldset > .amplify-flex:not(:has(button[type='submit'])), [data-amplify-authenticator-confirmresetpassword] fieldset > .amplify-flex:not(:has(button[type='submit']))":
                {
                    gap: tokens.SizingSpacing150,
                    marginBottom: AUTH_FORM_SECTION_MARGIN_BOTTOM,
                },
            '[data-amplify-authenticator-forcenewpassword] fieldset > .amplify-flex:not(:has(button[type="submit"]))': {
                gap: `${tokens.SizingSpacing100} !important`,
                marginBottom: AUTH_FORM_SECTION_MARGIN_BOTTOM,
            },
            '[data-amplify-authenticator-forcenewpassword] fieldset > .amplify-flex:not(:has(button[type="submit"])) > .amplify-field': {
                marginBottom: tokens.SizingSpacingNone,
            },
            "[data-amplify-authenticator-signin] button[type='submit']": {
                alignSelf: 'flex-end',
                ...authActionButtonStyles,
            },
            "[data-amplify-authenticator-forgotpassword] button[type='submit']": {
                ...authActionButtonStyles,
            },
            "[data-amplify-authenticator-confirmresetpassword] button[type='submit']": {
                ...authActionButtonStyles,
            },
            "[data-amplify-authenticator-forcenewpassword] button[type='submit']": {
                ...authActionButtonStyles,
                alignSelf: 'center',
                gridColumn: '2',
                justifySelf: 'stretch',
                margin: tokens.SizingSpacingNone,
                width: '100%',
            },
            "[data-amplify-authenticator-forcenewpassword] button[type='button']": {
                alignSelf: 'center',
                gridColumn: '1',
                justifySelf: 'start',
                margin: tokens.SizingSpacingNone,
                width: 'auto',
            },
            "[data-amplify-authenticator-forgotpassword] fieldset > .amplify-flex:has(button[type='submit']) button[type='button'], [data-amplify-authenticator-confirmresetpassword] fieldset > .amplify-flex:has(button[type='submit']) button[type='button']":
                {
                    ...getAuthLinkButtonStyles(),
                },
            "[data-amplify-authenticator-forcenewpassword] fieldset > button[type='button']": {
                ...getAuthLinkButtonStyles(),
                alignSelf: 'center',
                background: 'transparent',
                border: 'none',
                boxShadow: 'none',
                lineHeight: authActionButtonStyles.lineHeight,
            },
            // Amplify's Flex sets flex-direction via an inline `style` attribute, which beats a plain CSS rule
            "[data-amplify-authenticator-forgotpassword] fieldset > .amplify-flex:has(button[type='submit']), [data-amplify-authenticator-confirmresetpassword] fieldset > .amplify-flex:has(button[type='submit'])":
                {
                    flexDirection: 'row-reverse !important',
                    justifyContent: 'space-between !important',
                    alignItems: 'center !important',
                },
        }}
    />
)

const StyledAuthenticator = styled(Authenticator)`
    width: 100%;
    min-height: 100vh;
    overflow: hidden;
`

const AmplifyUiTheme: AmplifyTheme = {
    name: 'einwert theme',
    tokens: {
        colors: {
            primary: {
                '10': tokens.ColorSurfacePositiveDefault,
                '20': tokens.ColorSurfacePositiveInversePressed,
                '80': tokens.ColorSurfacePositiveBoldDefault,
                '90': tokens.ColorSurfacePositiveBoldHover,
                '100': tokens.ColorSurfacePositiveBoldPressed,
            },
            font: {
                primary: tokens.ColorTextDefault,
                secondary: tokens.ColorTextDefault,
                interactive: tokens.ColorTextDefault,
            },
            border: {
                primary: tokens.ColorBorderDefault,
            },
        },
        components: {
            fieldcontrol: {
                borderRadius: tokens.SizingCornerS,
                borderWidth: tokens.SizingBorderS,
            },
            authenticator: {
                router: {
                    borderColor: tokens.ColorBorderDefault,
                    borderWidth: tokens.SizingBorderS,
                    boxShadow: tokens.Popup.boxShadow,
                },
                container: {
                    widthMax: AUTH_CARD_WIDTH,
                },
                form: {
                    padding: AUTH_FORM_PADDING,
                },
            },
        },
    },
}

export { AuthenticatedApp }
